import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditSurchargesMutation,
  useGetSurchargesByMonthQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import { TimeframeSwitcher } from 'common/components/TimeframeSwitcher/TimeframeSwitcher';
import { useTimeframeSwitcherState } from 'common/components/TimeframeSwitcher/useTimeframeSwitcherState';
import { DateOnlyFormat } from 'common/models/dateType';
import * as notifier from 'common/services/notification';
import {
  SurchagesTableRows,
  useSurchargesTableData,
} from 'features/settings-view/hooks/useSurchargesTableData';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ratesSchema } from 'utils/schemas/settings';
import {
  formatDefaultSurcharges,
  toEditSurchargesDto,
} from '../utils/functions';
import { EffectiveText, MonthSwitcherWrapper } from './styles';

/** Part of the dynamic key passed to `useForm` for a bin surcharge. */
export const perBinStr = 'perBin';
/** Part of the dynamic key passed to `useForm` for a load surcharge. */
export const perLoadStr = 'perLoad';

/**
 * The type of the object passed to `useForm`.
 *
 * @remarks The keys of this object are dynamically generated by joining
 * the following ordered strings together in snake case:
 * - A {@link perBinStr} or {@link perLoadStr} to indicate the surcharge type,
 * - The surcharge range ID (aka the `haulLabelId`),
 * - The nullable surcharge ID.
 * This strategy helps map the form values to their correct input and also
 * provides the metadata needed when submitting the form. For this to work
 * though, it's important that the input controls on the form are named
 * with these same keys, so that `useForm` can bind the values correctly.
 *
 * See {@link formatDefaultSurcharges}, {@link toEditSurchargesDto}, and
 * {@link useSurchargesTableData} for details.
 */
export type SurchargeValues = Record<string, number | null>;

export const Surcharges: FC = () => {
  const { state: month } = useTimeframeSwitcherState(
    'fuel-surcharges',
    'month',
  );
  const firstMonday = month.firstMonday.format(DateOnlyFormat);
  const monthName = month.firstMonday.format('MMMM');

  const {
    data: surcharges,
    isLoading,
    isFetching,
    error,
  } = useGetSurchargesByMonthQuery({ firstMonday });
  const [editSurcharges, { isLoading: isSubmitting }] =
    useEditSurchargesMutation();
  const period = surcharges?.find(fsc => fsc.startDate && fsc.endDate);
  const { columns, data, isEditing, setIsEditing } = useSurchargesTableData(
    surcharges || [],
    isSubmitting,
  );
  const methods = useForm<SurchargeValues>({
    // We use `values` instead of `defaultValues` here because surcharges are
    // fetched through a hook that generates reactive values.
    values: formatDefaultSurcharges(surcharges || []),
    resolver: yupResolver(ratesSchema),
    mode: 'all',
  });
  const {
    reset,
    formState: { isDirty },
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: SurchargeValues): Promise<void> => {
    const valuesToArr = Object.entries(data);

    if (!isDirty || !valuesToArr.some(v => v !== null)) {
      // No values to save.
      closeForm();
      return;
    }

    const dto = toEditSurchargesDto(period?.periodId || null, data);
    try {
      await editSurcharges({ firstMonday, dto }).unwrap();
      notifier.showSuccessMessage(`${monthName} effective surcharges updated.`);
      closeForm();
    } catch (e) {
      handleError(e, `Unable to edit ${monthName} effective surcharges.`);
    }
  };

  const MonthSwitcher = () => (
    <MonthSwitcherWrapper>
      <TimeframeSwitcher
        identifier='fuel-surcharges'
        timeframe='month'
        disableButtons={isEditing}
      />
      {period ? (
        <EffectiveText>
          Effective: {moment(period.startDate).format('MM/DD')} -{' '}
          {moment(period.endDate).format('MM/DD')}
        </EffectiveText>
      ) : (
        <EffectiveText>No effective surcharges.</EffectiveText>
      )}
    </MonthSwitcherWrapper>
  );

  const tableProps: DataTableProps<SurchagesTableRows, SurchargeValues> = {
    columns,
    data,
    isLoading,
    isFetching,
    toolBar: <MonthSwitcher />,
    maxTableWidth: '900px',
    formProps: {
      methods,
      onSubmit,
      onCancel: closeForm,
      isEditing,
      setIsEditing,
      isDirty,
      isSubmitting,
    },
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch fuel surcharges for this month.');
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
